'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function CategoryHeader({ category }: { category: Category }) {
  const { t } = useTranslations();
  const description = category?.description ?? category?.short_description;

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [category?.slug]);

  useEffect(() => {
    if (isExpanded) return undefined;

    const checkOverflow = () => {
      const el = descriptionRef.current;
      if (!el) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [isExpanded, description]);

  return (
    <section className={cn('relative flex w-full flex-col items-center justify-center')}>
      <div className={cn('custom-section', 'bg-[#FCFBFF] pb-10 pt-10 md:pb-2 lg:pt-[50px]')}>
        <div
          className={cn('flex w-full flex-col items-start justify-start gap-6', 'custom-container')}
        >
          {!!category?.breadcrumb && (
            <Breadcrumbs
              breadcrumb={category?.breadcrumb}
              className={{
                wrapper: 'w-full items-start justify-start',
                list: 'w-full items-start justify-start',
              }}
            />
          )}
          <h1 className={cn('h1', 'text-left font-semibold tracking-tighter text-foreground')}>
            {category.name}
          </h1>
          {!!description && (
            <div className="flex w-full max-w-[894px] flex-col items-start justify-start gap-2">
              <Paragraph
                ref={descriptionRef}
                className={cn('w-full', !isExpanded && 'line-clamp-2')}
                color="foreground"
                size="base"
                innerHtml={description}
              />
              {isOverflowing && (
                <span
                  onClick={() => setIsExpanded(prev => !prev)}
                  className="cursor-pointer text-sm font-medium text-primary transition-all hover:opacity-70 sm:text-base"
                >
                  {isExpanded ? t('text604') : t('text1360')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <Image
        src={'/images/subpage-header-vector.svg'}
        alt="Vector"
        width={0}
        height={0}
        className="h-auto w-full object-contain object-center"
      />
    </section>
  );
}
